import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { FormSelect } from "react-bootstrap";
import DismissableModal from "components/DismissableModal";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { SWITCH_DISPOSITION } from "rbac/RbacContentProvider";
import UsabilityContext from "lookup/UsabilityContext";
import { buildSubmitForReviewRequestBody } from "views/Raw/FaxDisposition/FaxDispositionService";

const RawSubmitForReview = ({
  disallowEditing,
  onSubmitForReview,
  contextIsRaw = true,
  elementIdPrefix = "switchdisposition",
}) => {
  const [isCommentValid, setIsCommentValid] = useState(false);
  const [comments, setComments] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isUserSelected, setIsUserSelected] = useState(false);
  const [usersLoadFailed, setUsersLoadFailed] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(true);
  const modalRef = useRef(null);

  useEffect(() => {
    setIsUsersLoading(true);
    setUsersLoadFailed(false);

    sendAuthenticatedAsyncRequest(
      "/user?exclude_requester=true&context=raw",
      "GET",
      null,
      (response) => {
        setUsersList(Array.isArray(response.data) ? response.data : []);
        setIsUsersLoading(false);
      },
      () => {
        setUsersList([]);
        setUsersLoadFailed(true);
        setIsUsersLoading(false);
      }
    );
  }, []);

  const submitForReview = (closeModal) => {
    if (!isCommentValid || !isUserSelected) {
      return;
    }

    onSubmitForReview(
      buildSubmitForReviewRequestBody({
        reviewerId: selectedUserId,
        note: comments,
      })
    );
    closeModal();
  };

  const canSubmit = isCommentValid && isUserSelected;
  const usabilityContext = contextIsRaw
    ? UsabilityContext.RAW
    : UsabilityContext.SWIM;

  const customFooterContent = (
    <div>
      <Button
        id={`${elementIdPrefix}-sfr-modal-btn-cancel`}
        variant="outline-dark"
        onClick={() => modalRef.current.handleClose()}
      >
        Cancel
      </Button>
      <Button
        id={`${elementIdPrefix}-sfr-modal-btn-submit`}
        className="left-spacer"
        variant="warning"
        onClick={() => submitForReview(modalRef.current.handleClose)}
        disabled={!canSubmit}
      >
        Yes, submit this response for review
      </Button>
    </div>
  );

  return (
    <DismissableModal
      ref={modalRef}
      customFooterContent={customFooterContent}
      openModalButtonText="Submit for Review"
      openModalButtonStyle="warning"
      openModalButtonClassName="rm-orange btn-text-white"
      openModalButtonBlock={true}
      openModalButtonDisabled={disallowEditing}
      openModalButtonId={`${elementIdPrefix}-btn-sfr`}
      title="Submit Response for Review"
    >
      <p className="confirmation-text">
        You have selected to <b>submit</b> this response for review.
      </p>
      <p className="text-muted">
        Once you submit this response for review, the reviewer will claim this
        RAW and would be able to change its status.
      </p>

      {isUsersLoading ? (
        <p className="text-info">Loading operators...</p>
      ) : usersLoadFailed ? (
        <p className="text-danger">Failed to load operators</p>
      ) : (
        <Form.Group className="mb-3">
          <Form.Label>Select Reviewer</Form.Label>
          <FormSelect
            id={`${elementIdPrefix}-sfr-reviewer`}
            value={selectedUserId}
            onChange={(event) => {
              setSelectedUserId(event.target.value);
              setIsUserSelected(event.target.value !== "");
            }}
          >
            <option value="">Click to select</option>
            {usersList.map((user) => (
              <option value={user.id} key={user.id}>
                {user.name}
              </option>
            ))}
            {SWITCH_DISPOSITION.provideSubmitToPaw(usabilityContext)}
          </FormSelect>
          {!isUserSelected ? (
            <span className="text-danger smaller-feedback">
              Selecting a Reviewer is mandatory!
            </span>
          ) : (
            <span className="text-success smaller-feedback">Looks good!</span>
          )}
        </Form.Group>
      )}

      <Form.Group>
        <Form.Label>Please enter comments for reviewer</Form.Label>
        <Form.Control
          id={`${elementIdPrefix}-sfr-comments`}
          as="textarea"
          rows="3"
          value={comments}
          onChange={(event) => {
            setComments(event.target.value);
            setIsCommentValid(event.target.value !== "");
          }}
        />
        {!isCommentValid ? (
          <span className="text-danger smaller-feedback">
            Comments are mandatory!
          </span>
        ) : (
          <span className="text-success smaller-feedback">Looks good!</span>
        )}
      </Form.Group>
    </DismissableModal>
  );
};

export default RawSubmitForReview;
